import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconArrowUpRight,
  IconBrandWhatsapp,
  IconFlask,
  IconX,
} from "@tabler/icons-react";
import WhatsAppContact, {
  WhatsAppContactHandle,
} from "@/components/ui/molecules/WhatsAppContact";
import MDialog from "@/components/ui/atoms/MDialog";
import MDocumentUploadOptions from "@/components/ui/molecules/MDocumentUploadOptions";
import styles from "./FeatureCards.module.css";

/** Stable keys for each promo card, used for per-card dismissal. */
export const FEATURE_CARD_KEYS = ["labInsights", "maatraWhatsapp"] as const;

interface FeatureCardsProps {
  /** Keys of cards already dismissed (hidden). */
  dismissed: string[];
  /** Dismiss a single card by key (on X tap or card tap). */
  onDismiss: (key: string) => void;
  /**
   * Fill the available width instead of the default 90%. Used for inexperienced
   * users, whose dashboard lacks the centering widgets container.
   */
  fullWidth?: boolean;
  /** When false, tapping Lab Insights shows the "no blood report" dialog instead of navigating. */
  labInsightsReady?: boolean;
}

const FeatureCards: React.FC<FeatureCardsProps> = ({
  dismissed,
  onDismiss,
  fullWidth = false,
  labInsightsReady = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const whatsAppRef = useRef<WhatsAppContactHandle>(null);
  const [showLabInsightsDialog, setShowLabInsightsDialog] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);

  const handleAddBloodReport = () => {
    setShowLabInsightsDialog(false);
    setShowUploadOptions(true);
  };

  const cards = [
    {
      key: "labInsights",
      title: t("dashboard.featureCards.labInsights.title"),
      description: t("dashboard.featureCards.labInsights.description"),
      cta: t("dashboard.featureCards.labInsights.cta"),
      watermark: <IconFlask size={116} stroke={1.5} />,
      onClick: () => {
        if (labInsightsReady) {
          navigate("/lab-insights");
        } else {
          setShowLabInsightsDialog(true);
        }
      },
      dismissOnClick: false,
    },
    {
      key: "maatraWhatsapp",
      title: t("dashboard.featureCards.maatraWhatsapp.title"),
      description: t("dashboard.featureCards.maatraWhatsapp.description"),
      cta: t("dashboard.featureCards.maatraWhatsapp.cta"),
      watermark: <IconBrandWhatsapp size={116} stroke={1.5} />,
      onClick: () => whatsAppRef.current?.send(),
      // WhatsApp dismisses only once it actually opens (see onOpened below), so
      // the add-mobile flow can still show when this is the last visible card.
      dismissOnClick: false,
    },
  ];

  const visibleCards = cards.filter((card) => !dismissed.includes(card.key));

  return (
    <>
      <div
        className={`${styles.featureCards} ${
          fullWidth ? styles.featureCardsFullWidth : ""
        }`}
      >
        {visibleCards.map((card) => (
          <div key={card.key} className={styles.featureCardSlot}>
            <button
              type="button"
              className={styles.featureCard}
              onClick={() => {
                card.onClick();
                if (card.dismissOnClick) onDismiss(card.key);
              }}
            >
              <span className={styles.featureCardWatermark}>
                {card.watermark}
              </span>
              <span className={styles.featureCardTop}>
                <span className={styles.featureCardBadge}>
                  {t("dashboard.featureCards.justLaunched")}
                </span>
                <span className={styles.featureCardText}>
                  <span className={styles.featureCardTitle}>{card.title}</span>
                  <span className={styles.featureCardDescription}>
                    {card.description}
                  </span>
                </span>
              </span>
              <span className={styles.featureCardCta}>
                {card.cta}
                <IconArrowUpRight size={15} stroke={2} />
              </span>
            </button>
            <button
              type="button"
              className={styles.featureCardDismiss}
              aria-label={t("common.close")}
              onClick={() => onDismiss(card.key)}
            >
              <IconX size={20} stroke={2} color="#fff"/>
            </button>
          </div>
        ))}
      </div>
      <WhatsAppContact ref={whatsAppRef} />
      <MDialog
        isOpen={showLabInsightsDialog}
        onClose={() => setShowLabInsightsDialog(false)}
        title="No blood report yet"
        description={t("labInsights.emptyStateDescription")}
        primaryCtaText={t("labInsights.emptyStateCTA")}
        onPrimaryCta={handleAddBloodReport}
      />
      <MDocumentUploadOptions
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />
    </>
  );
};

export default FeatureCards;
